import type { CallJourneyResponse } from "@starsach/shared-types";

/**
 * requirements.md 8.5: one timeline per contact. Everything the assembler
 * returns is timestamped, so the trace is a re-reading of the journey,
 * not new data: legs as lanes, flow steps, bot turns and tool calls as
 * spans, the queue wait and the agent leg from the contact record, and
 * the handoff - the gap between the last thing the caller did in the IVR
 * or bot and the moment an agent picked up - as a span of its own.
 */
export type SpanKind = "leg" | "step" | "bot" | "tool" | "queue" | "handoff" | "agent" | "acw";

export interface Span {
  id: string;
  lane: Lane;
  kind: SpanKind;
  /** Epoch milliseconds. */
  start: number;
  end: number;
  label: string;
  /** One line for the details card and the accessible name. */
  detail: string;
  error?: boolean;
}

export type Lane = "Legs" | "Flow" | "Bot" | "Tools" | "Queue" | "Handoff" | "Agent";
export const LANES: Lane[] = ["Legs", "Flow", "Bot", "Tools", "Queue", "Handoff", "Agent"];

export interface Trace {
  t0: number;
  t1: number;
  spans: Span[];
  /** Where the recording's offset 0 sits on the axis: the agent connect of the recorded leg, else that leg's start. */
  recordingOrigin?: number;
}

const ms = (iso: string | undefined) => (iso ? Date.parse(iso) : NaN);
const ok = (n: number) => Number.isFinite(n);
const secs = (n: number) => `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}s`;
export const minutes = (n: number) => (n < 60_000 ? secs(n) : `${Math.floor(n / 60_000)}m ${Math.round((n % 60_000) / 1000)}s`);

export function buildTrace(j: CallJourneyResponse): Trace {
  const spans: Span[] = [];
  const legs = j.legs ?? [];

  legs.forEach((l, i) => {
    const start = ms(l.initiationTimestamp);
    if (!ok(start)) return;
    const end = ok(ms(l.disconnectTimestamp)) ? ms(l.disconnectTimestamp) : start;
    spans.push({
      id: `leg:${l.contactId}`, lane: "Legs", kind: "leg", start, end,
      label: legs.length > 1 ? `Leg ${i + 1}` : "Contact",
      detail: [l.initiationMethod, l.channel, l.queueName && `queue ${l.queueName}`, l.disconnectReason && `ended: ${l.disconnectReason}`].filter(Boolean).join(" · "),
      error: !l.agentId && l.disconnectReason === "CUSTOMER_DISCONNECT" && ok(ms(l.enqueuedAt)),
    });
    const enq = ms(l.enqueuedAt);
    const agentAt = ms(l.connectedToAgentAt);
    if (ok(enq)) {
      const qEnd = ok(agentAt) ? agentAt : end;
      spans.push({
        id: `queue:${l.contactId}`, lane: "Queue", kind: "queue", start: enq, end: qEnd,
        label: l.queueName ? `Queue: ${l.queueName}` : "Queue",
        detail: ok(agentAt) ? `waited ${minutes(qEnd - enq)}` : `abandoned after ${minutes(qEnd - enq)}`,
        error: !ok(agentAt),
      });
    }
    if (ok(agentAt)) {
      const holds = l.agentHoldMs ? `, on hold ${minutes(l.agentHoldMs)}` : "";
      spans.push({ id: `agent:${l.contactId}`, lane: "Agent", kind: "agent", start: agentAt, end, label: "Agent", detail: `talked ${minutes(end - agentAt)}${holds}` });
      if (l.afterContactWorkMs) spans.push({ id: `acw:${l.contactId}`, lane: "Agent", kind: "acw", start: end, end: end + l.afterContactWorkMs, label: "After-contact work", detail: minutes(l.afterContactWorkMs) });
    }
  });

  const steps = j.timeline?.status === "ok" ? j.timeline.steps : [];
  steps.forEach((s, i) => {
    const start = ms(s.timestamp);
    if (!ok(start)) return;
    spans.push({
      id: `step:${i}`, lane: "Flow", kind: "step", start, end: start + (s.dwellMs ?? 0),
      label: s.identifier ?? s.moduleType,
      detail: [s.moduleType, s.flowName, s.dwellMs !== undefined && secs(s.dwellMs), s.error && "error"].filter(Boolean).join(" · "),
      error: s.error,
    });
  });

  const turns = j.bot?.status === "ok" ? j.bot.turns : [];
  turns.forEach((t, i) => {
    const start = ms(t.timestamp);
    if (!ok(start)) return;
    const next = ms(turns[i + 1]?.timestamp);
    spans.push({
      id: `bot:${i}`, lane: "Bot", kind: "bot", start, end: ok(next) ? next : start + 5000,
      label: t.intent ?? "no intent",
      detail: [t.utterance && `"${t.utterance}"`, t.fallback && "fallback"].filter(Boolean).join(" · ") || "bot turn",
      error: t.fallback,
    });
  });

  const invocations = j.lambdas?.status === "ok" ? j.lambdas.invocations : [];
  invocations.forEach((inv, i) => {
    const start = ms(inv.timestamp);
    if (!ok(start)) return;
    const failed = inv.logLines.some((l) => /\b(error|task timed out|unhandled)\b/i.test(l));
    spans.push({
      id: `tool:${i}`, lane: "Tools", kind: "tool", start, end: start + (inv.durationMs ?? 0),
      label: inv.functionName,
      detail: [inv.durationMs !== undefined && `${inv.durationMs} ms`, failed && "error in logs"].filter(Boolean).join(" · ") || "invocation",
      error: failed,
    });
  });

  // The handoff: from the last thing the caller did before the queue to the agent picking up - or to the end, if nobody did.
  const handoffLeg = legs.find((l) => ok(ms(l.enqueuedAt)));
  if (handoffLeg) {
    const enq = ms(handoffLeg.enqueuedAt);
    const before = spans.filter((s) => (s.kind === "bot" || s.kind === "step") && s.start < enq);
    const from = before.length ? Math.max(...before.map((s) => s.end)) : enq;
    const agentAt = ms(handoffLeg.connectedToAgentAt);
    const to = ok(agentAt) ? agentAt : ms(handoffLeg.disconnectTimestamp);
    if (ok(to) && to > from) {
      const bot = before.some((s) => s.kind === "bot");
      spans.push({
        id: "handoff", lane: "Handoff", kind: "handoff", start: from, end: to,
        label: bot ? "Bot to agent handoff" : "IVR to agent handoff",
        detail: ok(agentAt) ? `${minutes(to - from)} from last ${bot ? "bot turn" : "step"} to agent` : `dropped after ${minutes(to - from)}${handoffLeg.disconnectReason ? ` (${handoffLeg.disconnectReason})` : ""}`,
        error: !ok(agentAt),
      });
    }
  }

  const starts = spans.map((s) => s.start);
  const ends = spans.map((s) => s.end);
  const t0 = starts.length ? Math.min(...starts) : 0;
  const t1 = ends.length ? Math.max(...ends, t0 + 1000) : 1000;
  const recorded = legs.find((l) => l.agentId) ?? legs[0];
  const origin = recorded ? (ok(ms(recorded.connectedToAgentAt)) ? ms(recorded.connectedToAgentAt) : ms(recorded.initiationTimestamp)) : NaN;
  const trace: Trace = { t0, t1, spans: spans.sort((a, b) => a.start - b.start) };
  if (ok(origin)) trace.recordingOrigin = origin;
  return trace;
}
